import { Calendar } from 'app/design/primitives/calendar';
import { Column, Row } from 'app/design/primitives/box';
import { RemoteError } from 'app/design/primitives/remote-error';
import { Text } from 'app/design/primitives/text';
import { useIsExpired } from 'app/hooks/use-is-expired';
import { useNavigation } from 'app/hooks/navigation';
import { addDays, isAfter, isBefore, isSameDay, startOfDay, localeAwareFormat } from 'app/lib/date';
import { useCreateListingChannel } from 'app/network/chat/channel';
import { useCreateOneLoan } from 'app/network/loan';
import { useMyProfile } from 'app/network/profile';
import { useUser } from 'app/network/user';
import { navigateToChat } from 'app/screens/chat/navigator-helper';
import { getListingLoanDateConstraints } from 'app/lib/listing-loan-date-constraints';
import { useCalendar } from 'app/screens/shared/loan-dates-picker';
import { createTheme as createDSTheme } from 'app/shopify';
import type { Listing } from 'app/types/listing';
import { ListingSubtype, TransactionMode } from 'app/types/listing';
import { isDonationListing } from 'app/lib/listing';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { IconCalendar, IconChevronDown, IconChevronUp, IconCircleCheck, IconClock } from '@tabler/icons-react-native';
import { createContext, type ReactNode, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, View } from 'react-native';
import Animated, { FadeInUp, FadeOutUp, LinearTransition, useReducedMotion } from 'react-native-reanimated';
import { usePresentAppDownloadPrompt } from 'app/hooks/use-present-app-download-prompt';

const theme = createDSTheme();

type DatedLoan = NonNullable<Listing['loans']>[number] & { endDate: Date; startDate: Date };

function getActiveReservation(sortedLoans: DatedLoan[], today: Date) {
  const current = sortedLoans.find((loan) => loan.startDate <= today && loan.endDate >= today);
  if (!current) return undefined;

  let chainEnd = new Date(current.end);
  for (const loan of sortedLoans) {
    if (loan.endDate < today) continue;
    const chainEndDay = startOfDay(chainEnd);
    if (loan.startDate.getTime() <= addDays(chainEndDay, 1).getTime()) {
      if (loan.endDate.getTime() > chainEndDay.getTime()) chainEnd = new Date(loan.end);
    } else {
      break;
    }
  }
  return { ...current, end: chainEnd.toString() };
}

function getAvailabilitySummary({
  activeReservation,
  availableDaysThisWeek,
  nextReservation,
  t,
  weekLength,
}: {
  activeReservation?: DatedLoan;
  availableDaysThisWeek: number;
  nextReservation?: DatedLoan;
  t: ReturnType<typeof useTranslation>['t'];
  weekLength: number;
}) {
  const dateFormat = t('dateFormat_MMMMd', { ns: 'common' });
  if (activeReservation) {
    return t('availabilityCalendar.currentReservation', {
      date: localeAwareFormat(new Date(activeReservation.end), dateFormat),
    });
  }
  if (nextReservation) {
    return t('availabilityCalendar.nextReservation', {
      date: localeAwareFormat(nextReservation.startDate, dateFormat),
    });
  }
  if (availableDaysThisWeek === weekLength) return t('availabilityCalendar.noUpcomingReservation');
  return t('availabilityCalendar.availableDaysThisWeek', { count: availableDaysThisWeek });
}

function getSelectionLabel({
  selectedEndDate,
  selectedStartDate,
  t,
}: {
  selectedEndDate: Date | null;
  selectedStartDate: Date | null;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  if (!selectedStartDate || !selectedEndDate) return t('availabilityCalendar.selectionHint');

  const dateFormat = t('dateFormat_MMMMd', { ns: 'common' });
  if (isSameDay(selectedStartDate, selectedEndDate)) return localeAwareFormat(selectedStartDate, dateFormat);
  return t('availabilityCalendar.selectedRange', {
    start: localeAwareFormat(selectedStartDate, dateFormat),
    end: localeAwareFormat(selectedEndDate, dateFormat),
  });
}

function AvailabilityToggle({ expanded, onPress }: { expanded: boolean; onPress: () => void }) {
  const { t } = useTranslation('one-listing');
  const ToggleIcon = expanded ? IconChevronUp : IconChevronDown;

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ expanded }}
      hitSlop={8}
      onPress={onPress}
      style={styles.expandButton}
    >
      <Text color="text.interactive" fontWeight="700" variant="bodySmall">
        {t(expanded ? 'availabilityCalendar.viewLess' : 'availabilityCalendar.viewMore')}
      </Text>
      <ToggleIcon strokeWidth={ICON_STROKE_WIDTH} size={17} color={theme.colors['icon.interactive']} />
    </Pressable>
  );
}

export interface DateProposalAction {
  isSubmitting: boolean;
  onPress: () => void;
}

interface ListingAvailabilityCalendarProps {
  listing: Listing;
}

type ListingAvailabilityCalendarContextValue = ReturnType<typeof useCalendar> & {
  currentUserId?: Listing['userId'];
  dateProposalAction: DateProposalAction | null;
  mutationError: Error | null;
};

const ListingAvailabilityCalendarContext = createContext<ListingAvailabilityCalendarContextValue | null>(null);

export function ListingAvailabilityCalendarProvider({ children, listing }: { children: ReactNode; listing: Listing }) {
  const navigation = useNavigation();
  const authorize = useIsExpired();
  const { t } = useTranslation('one-listing');
  const { data: user } = useUser();
  const presentAppDownloadPrompt = usePresentAppDownloadPrompt();
  const { data: profile } = useMyProfile();
  const isSingleDate =
    listing.subtype === ListingSubtype.service ||
    isDonationListing(listing) ||
    listing.transactionMode === TransactionMode.sell;
  const calendar = useCalendar(listing, undefined, isSingleDate, profile?.unavailability, user?.id);

  const createChannel = useCreateListingChannel();
  const createLoan = useCreateOneLoan();
  const isSubmitting = createChannel.isPending || createLoan.isPending;
  const mutationError = createChannel.error ?? createLoan.error;
  const { selectedEndDate, selectedStartDate } = calendar;

  const submitProposal = () => {
    if (!selectedStartDate || !selectedEndDate) return;

    return createChannel
      .mutateAsync(listing)
      .then((channel) =>
        createLoan
          .mutateAsync({
            channelURL: channel.channel_url,
            start: selectedStartDate,
            end: selectedEndDate,
          })
          .then(() => channel),
      )
      .then((channel) => {
        navigateToChat(navigation, channel.channel_url, undefined, t('availabilityCalendar.proposalSent'));
        presentAppDownloadPrompt({
          context: 'date-proposal',
          firstName: channel.members.find((member) => member.user_id !== user?.id)?.firstname,
        });
      })
      .catch(() => {
        // Both mutations expose their error in the calendar.
      });
  };

  const dateProposalAction =
    selectedStartDate && selectedEndDate
      ? {
          isSubmitting,
          onPress: () => authorize(() => void submitProposal()),
        }
      : null;

  return (
    <ListingAvailabilityCalendarContext.Provider
      value={{ ...calendar, currentUserId: user?.id, dateProposalAction, mutationError }}
    >
      {children}
    </ListingAvailabilityCalendarContext.Provider>
  );
}

export function useListingDateProposalAction() {
  return useContext(ListingAvailabilityCalendarContext)?.dateProposalAction ?? null;
}

function VisitorDateSelection({
  expanded,
  listing,
  currentUserId,
  mutationError,
  selectionLabel,
  reduceMotion,
}: Pick<ListingAvailabilityCalendarContextValue, 'currentUserId' | 'mutationError'> & {
  expanded: boolean;
  listing: Listing;
  selectionLabel: string;
  reduceMotion: boolean;
}) {
  const { t } = useTranslation('one-listing');
  if (!(expanded && listing.userId !== currentUserId && (!listing.guardianId || listing.guardianId !== currentUserId)))
    return null;
  return (
    <Animated.View
      entering={reduceMotion ? undefined : FadeInUp.duration(240)}
      exiting={reduceMotion ? undefined : FadeOutUp.duration(160)}
      style={styles.expandedContent}
    >
      <View style={styles.selectionSummary}>
        <Column flex={1} gap="spacing.general.x0,5">
          <Text color="text.secondary" variant="bodySmall">
            {t('availabilityCalendar.yourDates')}
          </Text>
          <Text fontWeight="700" variant="bodyBase">
            {selectionLabel}
          </Text>
        </Column>
        <IconCalendar strokeWidth={ICON_STROKE_WIDTH} size={22} color={theme.colors['text.brand']} />
      </View>

      {mutationError ? <RemoteError error={mutationError} /> : null}
    </Animated.View>
  );
}

export function ListingAvailabilityCalendar({ listing }: ListingAvailabilityCalendarProps) {
  const { t } = useTranslation('one-listing');
  const reduceMotion = useReducedMotion();
  const [expanded, setExpanded] = useState(false);
  const today = startOfDay(new Date());
  const calendar = useContext(ListingAvailabilityCalendarContext);
  if (!calendar) throw new Error('ListingAvailabilityCalendar must be rendered inside its Provider');
  const {
    currentUserId,
    mutationError,
    onDatePress,
    selectableUnavailableDateRanges,
    selectableUnavailableDates,
    selectedEndDate,
    selectedStartDate,
    startOrEnd,
    unavailableDateRanges,
    unavailableDates,
    userCurrentLoanDates,
  } = calendar;
  const { minimumDate, maximumDate } = getListingLoanDateConstraints(listing);
  const isOutsideListingDates = (date: Date) =>
    Boolean((minimumDate && isBefore(date, minimumDate)) || (maximumDate && isAfter(date, maximumDate)));

  const week = Array.from({ length: 7 }, (_, index) => addDays(today, index));
  const sortedLoans = (listing.loans ?? [])
    .map((loan) => ({
      ...loan,
      startDate: startOfDay(new Date(loan.start)),
      endDate: startOfDay(new Date(loan.end)),
    }))
    .sort((left, right) => left.startDate.getTime() - right.startDate.getTime());

  const nextReservation = sortedLoans.find((loan) => loan.startDate >= today);
  const activeReservation = getActiveReservation(sortedLoans, today);
  const availableDaysThisWeek = week.filter(
    (date) =>
      !isOutsideListingDates(date) &&
      !unavailableDates.some((unavailableDate: Date) => isSameDay(unavailableDate, date)),
  ).length;
  const availabilitySummary = getAvailabilitySummary({
    activeReservation,
    availableDaysThisWeek,
    nextReservation,
    t,
    weekLength: week.length,
  });

  const handleDatePress = (date: Date) => {
    onDatePress(date);
    setExpanded(true);
  };

  const selectionLabel = getSelectionLabel({ selectedEndDate, selectedStartDate, t });

  return (
    <Animated.View
      layout={reduceMotion ? undefined : LinearTransition.duration(260)}
      style={styles.card}
      testID="listing-availability-calendar"
    >
      <Row alignItems="center" gap="spacing.general.x3">
        <View style={styles.headingIcon}>
          <IconCalendar strokeWidth={ICON_STROKE_WIDTH} size={22} color={theme.colors['text.brand']} />
        </View>
        <Column flex={1} gap="spacing.general.x0,5">
          <Text fontWeight="700" variant="headerXSmall">
            {t('availabilityCalendar.title')}
          </Text>
          <Text color="text.secondary" variant="bodySmall">
            {availabilitySummary}
          </Text>
        </Column>
      </Row>

      {expanded ? (
        <Animated.View entering={reduceMotion ? undefined : FadeInUp.duration(240)}>
          <Calendar
            initial={selectedStartDate ?? today}
            minCurrent={today}
            minDate={minimumDate}
            maxDate={maximumDate}
            onDatePress={onDatePress}
            selected={[selectedStartDate, selectedEndDate].filter((date): date is Date => Boolean(date))}
            selectableUnavailableDateRanges={selectableUnavailableDateRanges}
            selectableUnavailableDates={selectableUnavailableDates}
            startFromNow
            unavailableDateRanges={unavailableDateRanges}
            unavailableDates={unavailableDates}
            userCurrentLoanDates={userCurrentLoanDates}
            willNowChoose={startOrEnd}
          />
        </Animated.View>
      ) : (
        <Row justifyContent="space-between" style={styles.week}>
          {week.map((date) => {
            const unavailable =
              isOutsideListingDates(date) ||
              unavailableDates.some((unavailableDate: Date) => isSameDay(unavailableDate, date));
            const selected =
              Boolean(selectedStartDate && isSameDay(selectedStartDate, date)) ||
              Boolean(selectedEndDate && isSameDay(selectedEndDate, date));
            const accessibilityLabel = t(
              unavailable ? 'availabilityCalendar.dayUnavailable' : 'availabilityCalendar.dayAvailable',
              { date: localeAwareFormat(date, t('dateFormat_EEEEMMMMd', { ns: 'common' })) },
            );

            return (
              <Pressable
                accessibilityLabel={accessibilityLabel}
                accessibilityRole="button"
                accessibilityState={{ disabled: unavailable, selected }}
                disabled={unavailable}
                key={date.toISOString()}
                onPress={() => handleDatePress(date)}
                style={styles.weekDay}
              >
                <Text color="text.secondary" fontWeight="600" style={styles.weekDayName} variant="bodySmall">
                  {localeAwareFormat(date, 'EEEEE')}
                </Text>
                <View
                  style={[
                    styles.dayCircle,
                    unavailable ? styles.dayUnavailable : styles.dayAvailable,
                    selected ? styles.daySelected : undefined,
                  ]}
                >
                  <Text
                    color={selected ? 'text.inverse.primary' : unavailable ? 'text.tertiary' : 'text.success'}
                    fontWeight="700"
                    variant="bodyBase"
                  >
                    {date.getDate()}
                  </Text>
                  <View
                    style={[
                      styles.statusDot,
                      {
                        backgroundColor: selected
                          ? theme.colors['surface.default']
                          : unavailable
                            ? theme.colors['surface.brand.secondary-4.default']
                            : theme.colors['text.success'],
                      },
                    ]}
                  />
                </View>
              </Pressable>
            );
          })}
        </Row>
      )}

      <Row alignItems="center" justifyContent="space-between">
        <Row alignItems="center" gap="spacing.general.x3">
          <Row alignItems="center" gap="spacing.general.x1">
            <IconCircleCheck strokeWidth={ICON_STROKE_WIDTH} size={15} color={theme.colors['text.success']} />
            <Text color="text.secondary" variant="bodySmall">
              {t('availabilityCalendar.available')}
            </Text>
          </Row>
          <Row alignItems="center" gap="spacing.general.x1">
            <IconClock
              strokeWidth={ICON_STROKE_WIDTH}
              size={15}
              color={theme.colors['surface.brand.secondary-4.default']}
            />

            <Text color="text.secondary" variant="bodySmall">
              {t('availabilityCalendar.reserved')}
            </Text>
          </Row>
        </Row>

        <AvailabilityToggle expanded={expanded} onPress={() => setExpanded((value) => !value)} />
      </Row>

      <VisitorDateSelection
        expanded={expanded}
        listing={listing}
        currentUserId={currentUserId}
        mutationError={mutationError}
        selectionLabel={selectionLabel}
        reduceMotion={reduceMotion}
      />
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: theme.colors['surface.default'],
    borderColor: theme.colors['border.secondary'],
    borderRadius: theme.borderRadii['radius.xl'],
    borderWidth: StyleSheet.hairlineWidth,
    gap: theme.spacing['spacing.general.x4'],
    overflow: 'hidden',
    padding: theme.spacing['spacing.general.x4'],
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.06,
    shadowRadius: 12,
    elevation: 2,
  },
  headingIcon: {
    alignItems: 'center',
    backgroundColor: theme.colors['surface.brand.subtle'],
    borderRadius: 20,
    height: 40,
    justifyContent: 'center',
    width: 40,
  },
  week: {
    gap: theme.spacing['spacing.general.x1'],
  },
  weekDay: {
    alignItems: 'center',
    flex: 1,
    gap: theme.spacing['spacing.general.x1'],
  },
  weekDayName: {
    textTransform: 'uppercase',
  },
  dayCircle: {
    alignItems: 'center',
    aspectRatio: 1,
    borderRadius: 999,
    justifyContent: 'center',
    maxWidth: 42,
    position: 'relative',
    width: '100%',
  },
  dayAvailable: {
    backgroundColor: theme.colors['surface.success.subtle'],
  },
  dayUnavailable: {
    backgroundColor: theme.colors['surface.brand.secondary-4.subtle'],
    opacity: 0.72,
  },
  daySelected: {
    backgroundColor: theme.colors['surface.brand.default'],
  },
  statusDot: {
    borderRadius: 3,
    bottom: 5,
    height: 5,
    position: 'absolute',
    width: 5,
  },
  expandButton: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.spacing['spacing.general.x0,5'],
    paddingVertical: theme.spacing['spacing.general.x1'],
  },
  expandedContent: {
    borderTopColor: theme.colors['border.secondary'],
    borderTopWidth: StyleSheet.hairlineWidth,
    gap: theme.spacing['spacing.general.x4'],
    paddingTop: theme.spacing['spacing.general.x4'],
  },
  selectionSummary: {
    alignItems: 'center',
    backgroundColor: theme.colors['surface.brand.subtle'],
    borderRadius: theme.borderRadii['radius.lg'],
    flexDirection: 'row',
    gap: theme.spacing['spacing.general.x3'],
    padding: theme.spacing['spacing.general.x3'],
  },
});
